import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Outlet, useLocation } from 'react-router-dom'
import { createTheme, ThemeProvider } from '@mui/material/styles'
import AppHeader from '../../components/app-header/AppHeader'
import BottomDock from '../../components/bottom-dock/BottomDock'
import Footer from '../../components/footer/Footer'
import InfoSheet from '../../components/info-sheet/InfoSheet'
import { PandaPromo } from '../../components/panda-sign/PandaPromo'
import { useAppType } from '../../context/AppContext'
import { LayoutContext } from '../../context/LayoutContext'
import { IProduct } from '../../types/products'
import { APP_THEMES } from '../../utils/appTheme'
import { celebrate, useKonamiCode } from '../../utils/easterEggs'
import { getGeneralInfo } from '../../utils/generalInfo'
import { useAppUi } from '../../utils/useAppUi'
import { useDeviceDetect } from '../../utils/use-device-detect'
import { useRouteKind } from '../../utils/useRouteKind'
import './MainLayout.scss'

type Props = {
  cart: IProduct[]
}

const supportsViewTransitions =
  typeof document !== 'undefined' && 'startViewTransition' in document

const MainLayout = (props: Props) => {
  useAppUi()
  useKonamiCode(celebrate)
  const appType = useAppType()
  const generalInfo = getGeneralInfo()
  const { isMobile, isTablet } = useDeviceDetect()
  const isDesktop = !(isMobile || isTablet)
  const location = useLocation()
  const route = useRouteKind()
  const [searchValue, setSearchValue] = useState('')
  const [searchOpen, setSearchOpen] = useState(false)
  const [infoOpen, setInfoOpen] = useState(false)

  const showDock = !isDesktop && (route === 'home' || route === 'delivery')
  const showPromo =
    !!generalInfo.homePromoMessage && (isDesktop || showDock)

  const theme = useMemo(
    () =>
      createTheme({
        palette: { primary: { main: APP_THEMES[appType].primary } },
        shape: { borderRadius: 14 },
        typography: {
          fontFamily: "'Nunito', system-ui, sans-serif",
          button: { textTransform: 'none', fontWeight: 800 },
        },
        components: {
          MuiButton: {
            defaultProps: { disableElevation: true },
            styleOverrides: {
              root: { borderRadius: 999, minHeight: 48, fontSize: 15 },
            },
          },
        },
      }),
    [appType],
  )

  useEffect(() => {
    if (!location.pathname.includes('checkout')) {
      sessionStorage.removeItem('orderCompleted')
    }
  }, [location.pathname])

  useEffect(() => {
    if (route !== 'home') setSearchOpen(false)
  }, [route])

  const openInfo = useCallback(() => setInfoOpen(true), [])
  const closeInfo = useCallback(() => setInfoOpen(false), [])

  const layout = useMemo(
    () => ({ searchValue, setSearchValue, searchOpen, setSearchOpen, openInfo }),
    [searchValue, searchOpen, openInfo],
  )

  return (
    <ThemeProvider theme={theme}>
      <LayoutContext.Provider value={layout}>
        <div
          className={`main-layout main-layout--${route}${showDock ? ' has-dock' : ''}`}
          style={{ '--dock-height': showDock ? '86px' : '0px' } as React.CSSProperties}
        >
          <AppHeader route={route} isDesktop={isDesktop} />
          <main
            key={supportsViewTransitions ? undefined : location.pathname}
            className={`main-layout__body${supportsViewTransitions ? '' : ' page-enter'}`}
          >
            <Outlet context={{ searchValue }} />
          </main>
          {isDesktop && <Footer />}
          {showDock && <BottomDock cart={props.cart} route={route} />}
          {showPromo && <PandaPromo message={generalInfo.homePromoMessage!} />}
          <InfoSheet open={infoOpen} onClose={closeInfo} />
        </div>
      </LayoutContext.Provider>
    </ThemeProvider>
  )
}

export default MainLayout
